"use client";

import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { geoEqualEarth, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import worldData from "world-atlas/countries-110m.json";
import type { FeatureCollection, Geometry } from "geojson";

const WIDTH = 980;
const HEIGHT = 520;

const projection = geoEqualEarth()
  .scale(230)
  .center([0, 10])
  .translate([WIDTH * 0.42, HEIGHT / 2 - 20])
  .clipExtent([[0, 0], [WIDTH, HEIGHT - 50]]);

const pathGen = geoPath(projection);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const countries = feature(worldData as any, (worldData as any).objects.countries) as unknown as FeatureCollection<Geometry>;

const COUNTRY_CENTROIDS: Record<string, [number, number]> = {
  "India":                       [617, 184],
  "China":                       [673, 127],
  "Vietnam":                     [708, 211],
  "South Korea":                 [746, 128],
  "Malaysia":                    [726, 268],
  "United States of America":    [ 69,  96],
};

const ZOOM_SCALE = 1.85;

type Office = {
  name: string;
  country: string;
  coords: [number, number];
  type: "hq" | "office" | "partner";
  unit: string;
  focus: string[];
  labelOffset?: [number, number, "left" | "right"];
  modalSide?: "left" | "right" | "top" | "bottom";
};

const OFFICES: Office[] = [
  {
    name: "Stevensville, Maryland",
    country: "United States of America",
    coords: [-76.0788, 38.9732],
    type: "hq",
    unit: "Global Headquarters",
    focus: ["Leadership", "Engineering", "Operations"],
    labelOffset: [7, 3, "right"],
    modalSide: "right",
  },
  {
    name: "Cleveland, Ohio",
    country: "United States of America",
    coords: [-81.6944, 41.4993],
    type: "office",
    unit: "Sales Office",
    focus: ["Sales"],
    labelOffset: [7, 3, "right"],
    modalSide: "top",
  },
  {
    name: "Burlington, North Carolina",
    country: "United States of America",
    coords: [-79.4378, 36.0957],
    type: "office",
    unit: "Sales Office",
    focus: ["Sales"],
    labelOffset: [7, 3, "right"],
    modalSide: "bottom",
  },
  {
    name: "Little Rock, Arkansas",
    country: "United States of America",
    coords: [-92.2896, 34.7465],
    type: "office",
    unit: "Sales Office",
    focus: ["Sales"],
    labelOffset: [7, 3, "right"],
    modalSide: "bottom",
  },
  {
    name: "Ahmedabad, India",
    country: "India",
    coords: [72.5714, 23.0225],
    type: "office",
    unit: "Supply Management Office",
    focus: ["Supply Management"],
    labelOffset: [7, 3, "right"],
    modalSide: "left",
  },
  {
    name: "Hosur, India",
    country: "India",
    coords: [77.8253, 12.7409],
    type: "partner",
    unit: "MMI PL",
    focus: ["Manufacturing"],
    labelOffset: [-7, -10, "right"],
    modalSide: "top",
  },
  {
    name: "Chennai, India",
    country: "India",
    coords: [80.2707, 13.0827],
    type: "partner",
    unit: "Wolverine PL",
    focus: ["Manufacturing"],
    labelOffset: [7, 3, "right"],
    modalSide: "left",
  },
  {
    name: "Coimbatore, India",
    country: "India",
    coords: [76.9558, 11.0168],
    type: "partner",
    unit: "Partner Facility",
    focus: ["Manufacturing"],
    labelOffset: [-7, 12, "right"],
    modalSide: "left",
  },
  {
    name: "Hanoi, Vietnam",
    country: "Vietnam",
    coords: [105.8412, 21.0245],
    type: "office",
    unit: "Supply Management Office & Warehouse",
    focus: ["Supply Management", "Warehouse"],
    labelOffset: [7, 3, "right"],
    modalSide: "left",
  },
  {
    name: "Ho Chi Minh City, Vietnam",
    country: "Vietnam",
    coords: [106.6297, 10.8231],
    type: "office",
    unit: "Supply Management Office",
    focus: ["Supply Management"],
    labelOffset: [7, 3, "right"],
    modalSide: "left",
  },
  {
    name: "Shanghai, China",
    country: "China",
    coords: [121.4737, 31.2304],
    type: "office",
    unit: "Supply Management Office",
    focus: ["Supply Management"],
    labelOffset: [7, 3, "right"],
    modalSide: "left",
  },
  {
    name: "Dongguan, China",
    country: "China",
    coords: [113.7518, 23.0207],
    type: "office",
    unit: "Supply Management Office & Warehouse",
    focus: ["Supply Management", "Warehouse"],
    labelOffset: [-6, -10, "right"],
    modalSide: "left",
  },
  {
    name: "Jinan, China",
    country: "China",
    coords: [117.1201, 36.6512],
    type: "office",
    unit: "Supply Management Office",
    focus: ["Supply Management"],
    labelOffset: [7, 3, "right"],
    modalSide: "left",
  },
  {
    name: "Seoul, South Korea",
    country: "South Korea",
    coords: [126.978, 37.5665],
    type: "office",
    unit: "Supply Management Office",
    focus: ["Supply Management"],
    labelOffset: [7, 3, "right"],
    modalSide: "left",
  },
  {
    name: "Malaysia",
    country: "Malaysia",
    coords: [101.6869, 3.139],
    type: "office",
    unit: "Supply Management Office",
    focus: ["Supply Management"],
    labelOffset: [7, 3, "right"],
    modalSide: "bottom",
  },
];

const OFFICES_BY_COUNTRY = OFFICES.reduce<Record<string, Office[]>>((acc, o) => {
  (acc[o.country] ??= []).push(o);
  return acc;
}, {});

const ZOOMABLE_COUNTRIES = Object.keys(COUNTRY_CENTROIDS);

const TYPE_COLOR: Record<string, { dot: string; glow: string }> = {
  hq:      { dot: "#f97316", glow: "rgba(249,115,22,0.45)" },
  office:  { dot: "#facc15", glow: "rgba(250,204,21,0.35)" },
  partner: { dot: "#fb923c", glow: "rgba(251,146,60,0.35)" },
};

const TYPE_LABEL: Record<string, string> = {
  hq: "Headquarters",
  office: "Sales / Supply Office",
  partner: "Partner Facility",
};

function shortLabel(name: string) {
  return name
    .replace(/, India$/, "")
    .replace(/, Vietnam$/, "")
    .replace(/, China$/, "")
    .replace(/, South Korea$/, "")
    .replace(/, Malaysia$/, "")
    .replace(/, Ohio$/, "")
    .replace(/, Maryland$/, "")
    .replace(/, North Carolina$/, "")
    .replace(/, Arkansas$/, "");
}

function CityDots({
  offices,
  hoveredOffice,
  onHoverOffice,
  activeOffice,
  zoomed,
  onCountryHover,
  showLabels,
  zoom,
}: {
  offices: Office[];
  hoveredOffice: string | null;
  onHoverOffice: (n: string | null) => void;
  activeOffice: string | null;
  zoomed: boolean;
  onCountryHover?: (v: boolean) => void;
  showLabels: boolean;
  zoom: number;
}) {
  return (
    <>
      {offices.map((o) => {
        const pt = projection(o.coords);
        if (!pt) return null;
        const [x, y] = pt;
        if (y > HEIGHT - 60 || y < 0 || x < 0 || x > WIDTH) return null;
        const isHQ = o.type === "hq";
        const isHover = hoveredOffice === o.name || activeOffice === o.name;
        const baseR = isHQ ? (zoomed ? 3.5 : 5) : (zoomed ? 2.2 : 3.5);
        const r = isHover ? baseR + 1.2 : baseR;
        const { dot: dotColor, glow: glowColor } = TYPE_COLOR[o.type];
        const [dx, dy, anchor] = o.labelOffset ?? [7, 3, "right"];
        const label = shortLabel(o.name);
        const labelW = label.length * 5.6 + 10;
        const rectX = anchor === "right" ? dx : dx - labelW;

        return (
          <g
            key={o.name}
            transform={`translate(${x},${y})`}
            style={{ pointerEvents: "all", cursor: "pointer" }}
            onMouseEnter={() => { onCountryHover?.(true); onHoverOffice(o.name); }}
            onMouseLeave={() => { onHoverOffice(null); }}
          >
            <circle r={r + (isHover ? 8 : 5)} fill={glowColor} opacity={isHover ? 0.9 : 0.3}>
              <animate attributeName="r" values={`${r+3};${r+8};${r+3}`} dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.5;0.05;0.5" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <circle
              r={r}
              fill={dotColor}
              stroke="rgba(255,255,255,0.8)"
              strokeWidth={isHQ ? 1.2 : 0.8}
              style={{ transition: "r 150ms" }}
            />
            {showLabels && (
              <g
                style={{
                  opacity: zoomed ? 1 : 0,
                  transition: "opacity 220ms ease 60ms",
                  pointerEvents: "none",
                  transform: `scale(${1 / zoom})`,
                  transformBox: "fill-box",
                  transformOrigin: "0px 0px",
                }}
              >
                <rect
                  x={rectX}
                  y={dy - 9}
                  width={labelW}
                  height={14}
                  rx={3}
                  fill="rgba(0,0,0,0.82)"
                  stroke={`${dotColor}66`}
                  strokeWidth={0.8}
                />
                <text
                  x={rectX + 5}
                  y={dy + 1.5}
                  fontSize={9}
                  fill={dotColor}
                  fontFamily="system-ui, sans-serif"
                  fontWeight="700"
                  letterSpacing="0.04em"
                >
                  {label}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </>
  );
}

function BaseMap({
  hoveredOffice,
  onHoverOffice,
  activeOffice,
  zoomedCountry,
}: {
  hoveredOffice: string | null;
  onHoverOffice: (n: string | null) => void;
  activeOffice: string | null;
  zoomedCountry: string | null;
}) {
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const officeCountries = useMemo(() => new Set(OFFICES.map((o) => o.country)), []);

  const activeOfficeCountry = useMemo(() => {
    const name = hoveredOffice ?? activeOffice;
    return OFFICES.find((o) => o.name === name)?.country ?? null;
  }, [hoveredOffice, activeOffice]);

  const highlightedCountry = activeOfficeCountry ?? hoveredCountry;

  const zoomableSet = new Set(ZOOMABLE_COUNTRIES);
  const baseFeatures = useMemo(
    () => countries.features.filter((f) => !zoomableSet.has((f as any).properties?.name)),
    []
  );

  const nonZoomableOffices = OFFICES.filter((o) => !COUNTRY_CENTROIDS[o.country]);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      style={{
        width: "100%", height: "100%", display: "block",
        opacity: zoomedCountry ? 0.4 : 1,
        transition: "opacity 350ms ease",
      }}
    >
      <rect x={0} y={0} width={WIDTH} height={HEIGHT} fill="var(--map-ocean)" />
      <g>
        {baseFeatures.map((f, i) => {
          const d = pathGen(f);
          if (!d) return null;
          const name = (f as any).properties?.name as string | undefined;
          const hasOffice = name && officeCountries.has(name);
          const isHighlighted = name && highlightedCountry === name;
          return (
            <path key={i} d={d}
              fill={isHighlighted
                ? "color-mix(in oklab, var(--map-accent) 30%, var(--map-land))"
                : hasOffice
                  ? "color-mix(in oklab, var(--map-accent) 12%, var(--map-land))"
                  : "var(--map-land)"}
              stroke="var(--map-stroke)"
              strokeWidth={isHighlighted ? 0.9 : 0.5}
              onMouseEnter={() => name && setHoveredCountry(name)}
              onMouseLeave={() => setHoveredCountry(null)}
              style={{ cursor: hasOffice ? "pointer" : "default", transition: "fill 200ms" }}
            />
          );
        })}
      </g>
      <CityDots
        offices={nonZoomableOffices}
        hoveredOffice={hoveredOffice}
        onHoverOffice={onHoverOffice}
        activeOffice={activeOffice}
        zoomed={false}
        showLabels={false}
        zoom={1}
      />
    </svg>
  );
}

function CountryOverlay({
  countryName,
  zoomed,
  dimmed,
  onHover,
  hoveredOffice,
  onHoverOffice,
  activeOffice,
}: {
  countryName: string;
  zoomed: boolean;
  dimmed: boolean;
  onHover: (v: boolean) => void;
  hoveredOffice: string | null;
  onHoverOffice: (n: string | null) => void;
  activeOffice: string | null;
}) {
  const [cx, cy] = COUNTRY_CENTROIDS[countryName];
  const pivotX = (cx / WIDTH) * 100;
  const pivotY = (cy / HEIGHT) * 100;

  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleEnter = () => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    onHover(true);
  };
  const handleLeave = () => {
    leaveTimer.current = setTimeout(() => { onHover(false); leaveTimer.current = null; }, 140);
  };
  useEffect(() => () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); }, []);

  const countryFeature = useMemo(
    () => countries.features.find((f) => (f as any).properties?.name === countryName),
    [countryName]
  );
  const countryPath = useMemo(() => countryFeature ? pathGen(countryFeature) : null, [countryFeature]);
  const bbox = useMemo(() => countryFeature ? pathGen.bounds(countryFeature) : null, [countryFeature]);
  const officesForCountry = OFFICES_BY_COUNTRY[countryName] ?? [];

  return (
    <div
      style={{
        position: "absolute", inset: 0,
        pointerEvents: "none",
        transform: zoomed ? `scale(${ZOOM_SCALE})` : "scale(1)",
        transformOrigin: `${pivotX}% ${pivotY}%`,
        transition: "transform 420ms cubic-bezier(0.34,1.56,0.64,1)",
        opacity: dimmed ? 0.35 : 1,
        zIndex: zoomed ? 3 : 1,
      }}
    >
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{ width: "100%", height: "100%", display: "block" }}
      >
        <defs>
          <filter id={`shadow-${countryName.replace(/\s/g, "")}`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="12"
              floodColor="#f97316" floodOpacity={zoomed ? 0.45 : 0} />
          </filter>
        </defs>

        {bbox && (
          <rect
            x={bbox[0][0] - 4}
            y={bbox[0][1] - 4}
            width={bbox[1][0] - bbox[0][0] + 8}
            height={bbox[1][1] - bbox[0][1] + 8}
            fill="transparent"
            style={{ pointerEvents: "all", cursor: "pointer" }}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
          />
        )}

        {countryPath && (
          <path
            d={countryPath}
            fill={zoomed
              ? "color-mix(in oklab, var(--map-accent) 38%, var(--map-land))"
              : "color-mix(in oklab, var(--map-accent) 13%, var(--map-land))"}
            stroke={zoomed ? "rgba(249,115,22,0.75)" : "var(--map-stroke)"}
            strokeWidth={zoomed ? 1.4 : 0.6}
            filter={zoomed ? `url(#shadow-${countryName.replace(/\s/g, "")})` : undefined}
            style={{ pointerEvents: "all", cursor: "pointer", transition: "fill 280ms, stroke 280ms" }}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
          />
        )}

        <CityDots
          offices={officesForCountry}
          hoveredOffice={hoveredOffice}
          onHoverOffice={onHoverOffice}
          activeOffice={activeOffice}
          zoomed={zoomed}
          onCountryHover={onHover}
          showLabels={true}
          zoom={ZOOM_SCALE}
        />
      </svg>
    </div>
  );
}

export default function WorldPresenceMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredOffice, setHoveredOffice] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const countryLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setCountryHovered = useCallback((country: string | null) => {
    if (countryLeaveTimer.current) { clearTimeout(countryLeaveTimer.current); countryLeaveTimer.current = null; }
    if (country !== null) {
      setHoveredCountry(country);
    } else {
      countryLeaveTimer.current = setTimeout(() => { setHoveredCountry(null); }, 140);
    }
  }, []);

  useEffect(() => {
    if (paused || hoveredOffice) return;
    const id = setInterval(() => setActiveIndex((i) => (i + 1) % OFFICES.length), 2600);
    return () => clearInterval(id);
  }, [paused, hoveredOffice]);

  const activeOffice = hoveredOffice ?? OFFICES[activeIndex].name;

  const hoveredOfficeCountry = hoveredOffice
    ? (OFFICES.find((o) => o.name === hoveredOffice)?.country ?? null)
    : null;
  const zoomedCountry = hoveredOfficeCountry ?? hoveredCountry;

  const stats = useMemo(() => ({
    countries: new Set(OFFICES.map((o) => o.country)).size,
    cities: OFFICES.length,
  }), []);

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--map-accent)]">
            Global Operations
          </p>
          <h2 className="mt-4 text-[22px] font-semibold text-white md:text-4xl">
            Global presence. Unified execution.
          </h2>
          <p className="mt-4 max-w-3xl text-[13.5px] leading-[1.7] text-white/70 md:text-base">
            Headquarters in Stevensville, MD with sales offices across the US, partner facilities
            in India, and supply management operations in Vietnam, China, South Korea, and Malaysia.
          </p>
        </div>
        <div className="flex gap-8">
          <Stat label="Locations" value={stats.cities} />
          <Stat label="Countries" value={stats.countries} />
        </div>
      </div>

      <div
        ref={containerRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => { setPaused(false); setCountryHovered(null); }}
        className="relative overflow-hidden rounded-2xl border border-white/10 bg-[color:var(--map-bg)] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
      >
        <BaseMap
          hoveredOffice={hoveredOffice}
          onHoverOffice={setHoveredOffice}
          activeOffice={activeOffice}
          zoomedCountry={zoomedCountry}
        />

        {ZOOMABLE_COUNTRIES.map((country) => (
          <CountryOverlay
            key={country}
            countryName={country}
            zoomed={zoomedCountry === country}
            dimmed={!!zoomedCountry && zoomedCountry !== country}
            onHover={(v) => setCountryHovered(v ? country : null)}
            hoveredOffice={hoveredOffice}
            onHoverOffice={setHoveredOffice}
            activeOffice={activeOffice}
          />
        ))}

        <OfficeCard
          office={hoveredOffice ? (OFFICES.find((o) => o.name === hoveredOffice) ?? null) : null}
          containerRef={containerRef}
          zoomedCountry={zoomedCountry}
        />

        <div
          className="pointer-events-none absolute bottom-3 right-3 rounded-lg border border-white/10 bg-black/50 px-2.5 py-1.5 text-[10px] uppercase tracking-widest text-white/40 backdrop-blur-sm"
          style={{ opacity: zoomedCountry ? 0 : 1, transition: "opacity 300ms" }}
        >
          Hover a country to zoom
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-5 text-[13px] text-white/60">
        <LegendDot label="Headquarters" color="#f97316" big />
        <LegendDot label="Office" color="#facc15" />
        <LegendDot label="Partner" color="#fb923c" />
        <span className="ml-auto text-[11px] uppercase tracking-[0.18em] text-white/30">
          {paused ? "Paused" : "Auto-touring locations"}
        </span>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-[clamp(28px,3vw,48px)] font-light tabular-nums text-white leading-none">{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/50">{label}</div>
    </div>
  );
}

function LegendDot({ label, color, big }: { label: string; color: string; big?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block rounded-full"
        style={{ width: big ? 10 : 7, height: big ? 10 : 7, background: color, boxShadow: `0 0 10px ${color}88` }}
      />
      {label}
    </div>
  );
}

// ─── KEY FIX: OfficeCard now receives zoomedCountry and applies zoom math ─────
function OfficeCard({
  office,
  containerRef,
  zoomedCountry,
}: {
  office: Office | null;
  containerRef: React.RefObject<HTMLDivElement | null>;
  zoomedCountry: string | null;
}) {
  if (!office) return null;
  const rect = containerRef.current?.getBoundingClientRect();
  const cw = rect?.width ?? WIDTH;
  const ch = rect?.height ?? HEIGHT;
  const pt = projection(office.coords);
  if (!pt) return null;

  // Raw SVG-space dot position
  const [mx, my] = pt;

  // Scale from SVG viewBox to rendered container pixels
  const renderScale = Math.min(cw / WIDTH, ch / HEIGHT);
  const offsetX = (cw - WIDTH * renderScale) / 2;
  const offsetY = (ch - HEIGHT * renderScale) / 2;

  // Unzoomed screen position
  let x = offsetX + mx * renderScale;
  let y = offsetY + my * renderScale;

  // If this office's country is currently zoomed, apply the same CSS transform
  // that CountryOverlay applies so the card tracks the actual visual dot position.
  // CSS transform: scale(ZOOM_SCALE) from pivot (pivotX%, pivotY%) in container space.
  if (zoomedCountry && office.country === zoomedCountry && COUNTRY_CENTROIDS[zoomedCountry]) {
    const [svgPivotX, svgPivotY] = COUNTRY_CENTROIDS[zoomedCountry];
    // Pivot in container pixels
    const pivotScreenX = offsetX + svgPivotX * renderScale;
    const pivotScreenY = offsetY + svgPivotY * renderScale;
    // Apply scale around pivot
    x = pivotScreenX + (x - pivotScreenX) * ZOOM_SCALE;
    y = pivotScreenY + (y - pivotScreenY) * ZOOM_SCALE;
  }

  const CARD_W = 240;
  const CARD_APPROX_H = 130;

  const { dot: dotColor } = TYPE_COLOR[office.type];

  const side = office.modalSide ?? (x + CARD_W + 24 < cw ? "right" : "left");

  let left: number;
  let top: number;
  let translateY = "0%";

  if (side === "left") {
    left = x - CARD_W - 16;
    top = y - CARD_APPROX_H / 2;
  } else if (side === "right") {
    left = x + 16;
    top = y - CARD_APPROX_H / 2;
  } else if (side === "top") {
    left = x - CARD_W / 2;
    top = y - 16;
    translateY = "-100%";
  } else {
    // bottom
    left = x - CARD_W / 2;
    top = y + 16;
  }

  left = Math.max(8, Math.min(left, cw - CARD_W - 8));

  return (
    <div
      className="pointer-events-none absolute z-20 animate-in fade-in zoom-in-95 duration-150"
      style={{ left, top, width: CARD_W, transform: `translateY(${translateY})` }}
    >
      <div
        className="rounded-xl border border-white/15 bg-black/75 p-4 text-white backdrop-blur-md"
        style={{ boxShadow: `0 0 0 1px ${dotColor}44, 0 20px 50px -15px rgba(0,0,0,0.8)` }}
      >
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
          style={{ color: dotColor, background: `${dotColor}26`, border: `1px solid ${dotColor}66` }}
        >
          {TYPE_LABEL[office.type]}
        </span>
        <div className="mt-3">
          <div className="text-base font-semibold leading-tight">{office.name}</div>
          <div className="text-xs text-white/60">{office.unit}</div>
        </div>
        {office.focus.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {office.focus.map((f) => (
              <span key={f} className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-white/80">{f}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}